import type { SocialGridDocument, SocialMediaMetadata, SocialPerson, SocialProfile, SocialWidget } from '@daily/contracts/api';
import type { BgId, Hex, Lines, MediaKind, Member, Picture, Widget } from './types.js';

/**
 * Pure mapping between the backend's social documents and the v1 UI's widgets
 * (design-handoff/07 §3). No fetching, no clocks; addresses come from the caller.
 */

/** Everyone starts with this colour and picture until they choose their own (backend defaults). */
export const DEFAULT_COLOUR = '#6aaef0';

export type MediaVariant = 'w640' | 'w1280' | 'playback' | 'poster';
/** Where a media item can be loaded from right now, or null while no address is known. */
export type AddressOf = (assetId: string, variant: MediaVariant) => string | null;

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FONT_SIZES: readonly number[] = [12, 14, 16, 18, 21, 24, 28, 32, 38, 44];
const NAMED_BACKGROUNDS: readonly string[] = ['none', 'tile', 'light', 'frost', 'own', 'g:own', 'g:dusk', 'g:ember', 'g:tide', 'g:bloom', 'g:meadow'];
const ALT_MAX = 500;

export const isUuid = (value: unknown): value is string => typeof value === 'string' && UUID.test(value);
const clamp = (value: number, min: number, max: number) => Math.max(min, Math.min(max, value));
const round3 = (value: number) => Math.round(value * 1000) / 1000;

/** The aspect class of a photo, for the places that still read `kind`. */
export function kindOf(ar: number | undefined): MediaKind {
  if (!ar) return 'square';
  return ar > 1.05 ? 'landscape' : ar < 0.95 ? 'portrait' : 'square';
}

/**
 * Whether a stored picture is animated. The backend does not say (its image metadata has no such field), so
 * the stored name decides: a picture is saved under its file name, and only GIFs are kept animated. A GIF
 * whose name does not end in ".gif" is shown like a still photo: it plays, and it cannot be held.
 */
export const isAnimatedName = (name: string | undefined): boolean => /\.gif$/i.test((name ?? '').trim());

/** The backend's widgets as v1 items. Audio widgets become hidden `voice` items (G12: kept, cells reserved, not drawn). */
export function widgetsFromDocument(document: SocialGridDocument, assets: readonly SocialMediaMetadata[], address: AddressOf): Widget[] {
  return document.widgets.map((widget): Widget => {
    const { column: c, row: r, width: w, height: h } = widget.rect;
    if (widget.kind === 'text') {
      return {
        id: widget.id, t: 'text', c, r, w, h, text: widget.text,
        ...(widget.background !== undefined ? { bg: widget.background as BgId } : {}),
        ...(widget.fontSize !== undefined ? { fs: widget.fontSize } : {}),
      };
    }
    if (widget.kind === 'audio') return { id: widget.id, t: 'voice', c, r, w, h, mediaId: widget.assetId, name: widget.label };
    const meta = assets.find((asset) => asset.assetId === widget.assetId && asset.kind === widget.kind);
    const ar = meta && meta.kind !== 'audio' && meta.width > 0 && meta.height > 0 ? meta.width / meta.height : undefined;
    const base = {
      id: widget.id, c, r, w, h, mediaId: widget.assetId, name: widget.altText,
      px: widget.framing.focusX, py: widget.framing.focusY, z: widget.framing.zoom,
      ...(ar ? { ar } : {}),
    };
    if (widget.kind === 'video') {
      const len = meta?.kind === 'video' ? Math.max(1, Math.round(meta.durationMs / 1000)) : undefined;
      return { ...base, t: 'video', kind: 'video', url: address(widget.assetId, 'playback'), ...(len ? { len } : {}) };
    }
    return { ...base, t: 'photo', kind: kindOf(ar), url: address(widget.assetId, 'w1280'), ...(isAnimatedName(widget.altText) ? { anim: true } : {}) };
  });
}

/**
 * v1 items as a backend document. Widget ids the backend did not make (the UI's own) are replaced by
 * fresh UUIDs, the same one for the same id within one call. Media without a backend id cannot be
 * stored and is reported through `missing`. Songs are not stored (no song source in v1).
 */
export function documentFromWidgets(items: readonly Widget[], lines: Lines | undefined, newId: () => string): { document: SocialGridDocument; missing: number } {
  const ids = new Map<string, string>();
  const idOf = (id: string) => { if (isUuid(id)) return id.toLowerCase(); let fresh = ids.get(id); if (!fresh) { fresh = newId(); ids.set(id, fresh); } return fresh; };
  let missing = 0;
  const widgets: SocialWidget[] = [];
  for (const item of items) {
    const rect = { column: item.c, row: item.r, width: item.w, height: item.h };
    if (item.t === 'text') {
      const fontSize = item.fs !== undefined && FONT_SIZES.includes(item.fs) ? item.fs as 12 : undefined;
      const background = backgroundOf(item.bg);
      widgets.push({ id: idOf(item.id), kind: 'text', rect, text: (item.text ?? '').replace(/\r\n?/g, '\n'), ...(background ? { background } : {}), ...(fontSize ? { fontSize } : {}) });
    } else if (item.t === 'voice') {
      if (!isUuid(item.mediaId)) { missing += 1; continue; }
      widgets.push({ id: idOf(item.id), kind: 'audio', rect, assetId: item.mediaId.toLowerCase(), label: [...(item.name ?? '')].slice(0, ALT_MAX).join('') });
    } else if (item.t === 'photo' || item.t === 'video') {
      if (!isUuid(item.mediaId)) { missing += 1; continue; }
      widgets.push({
        id: idOf(item.id), kind: item.t === 'photo' ? 'image' : 'video', rect, assetId: item.mediaId.toLowerCase(),
        altText: [...(item.name ?? '')].slice(0, ALT_MAX).join(''),
        framing: { fit: 'cover', focusX: round3(clamp(item.px ?? 0.5, 0, 1)), focusY: round3(clamp(item.py ?? 0.5, 0, 1)), zoom: round3(clamp(item.z ?? 1, 1, 4)) },
      });
    }
  }
  return { document: { schemaVersion: 1, columns: 7, rows: 6, ...(lines ? { lines } : {}), widgets }, missing };
}

function backgroundOf(bg: BgId | undefined): string | undefined {
  if (bg === undefined) return undefined;
  if (NAMED_BACKGROUNDS.includes(bg)) return bg;
  return /^#[0-9a-f]{6}$/i.test(bg) ? bg.toLowerCase() : undefined;
}

/** A person's picture: a preset id, an uploaded picture's address, or nothing yet. */
export function pictureOf(person: SocialPerson | SocialProfile, address: AddressOf): Picture {
  if (person.avatarAssetId) {
    const url = address(person.avatarAssetId, 'w640');
    return url ? { url, mediaId: person.avatarAssetId } : '';
  }
  return person.avatar?.kind === 'preset' ? person.avatar.presetId : '';
}

export function colourOf(person: SocialPerson | SocialProfile): Hex {
  return person.accentColor && /^#[0-9a-f]{6}$/i.test(person.accentColor) ? person.accentColor.toLowerCase() : DEFAULT_COLOUR;
}

export function memberOf(person: SocialPerson | SocialProfile, meId: string | null, address: AddressOf): Member {
  return { id: person.userId, name: person.displayName, handle: person.handle, color: colourOf(person), pic: pictureOf(person, address), isYou: person.userId === meId };
}

/** The media a set of documents needs addresses for. */
export function mediaOf(document: SocialGridDocument, assets: readonly SocialMediaMetadata[]): { assetId: string; variant: MediaVariant }[] {
  return document.widgets.flatMap((widget) => {
    if (widget.kind !== 'image' && widget.kind !== 'video') return [];
    if (!assets.some((asset) => asset.assetId === widget.assetId)) return [];
    return [{ assetId: widget.assetId, variant: widget.kind === 'image' ? 'w1280' as const : 'playback' as const }];
  });
}

/** Two documents hold the same widgets and lines (ids included). */
export function sameDocument(a: SocialGridDocument | null | undefined, b: SocialGridDocument | null | undefined): boolean {
  return JSON.stringify(canonical(a)) === JSON.stringify(canonical(b));
}
function canonical(document: SocialGridDocument | null | undefined) {
  if (!document) return { lines: 'colour', widgets: [] };
  return { lines: document.lines ?? 'colour', widgets: document.widgets };
}
